<template>
  <div>
    <div class="post bg-green">
      <div class="image">
        <video autoplay playsinline loop src="@/assets/2026/civ3.mp4" style="height: 100%; width: 100%; object-fit: cover;" />
      </div>
      <div class="post-content-wrapper">
        <div class="post-header">Civilians and Gangs</div>
        <pre class="post-content">
          The remaining population of Nordland - traders, workers, officials, gang members, and survivors trying to maintain daily life amid conflict. Some cooperate with factions or criminal groups, others stay neutral, all struggling to keep society functioning in a nation falling apart.
        </pre>
      </div>
    </div>

    <div class="post">
      <div class="post-content-wrapper">
        <div class="post-header">Quick links</div>
        <div class="post-content">
          <ul class="quicklinks">
            <li><a href="#civilians">Civilians of Nordland</a></li>
            <li><a href="#committee">Civil Election Committee (CEC)</a></li>
            <li><a href="#observer">Election Observer & Worker</a></li>
            <li><a href="#politicians">Politicians</a></li>
            <li><a href="#bureau">Civil Protection Bureau (CPB)</a></li>
            <li><a href="#gangs">Gangs of Nordland</a></li>
            <li><a href="#wolves">Odin’s Wolves MC</a></li>
            <li><a href="#sons">Sons of the North</a></li>
          </ul>
        </div>
      </div>
    </div>

    <div id="civilians" class="post">
      <div class="post-content-wrapper">
        <div class="post-header">Civilians of Nordland</div>

        <pre class="post-content">
          The <b>civilians of Nordland</b> are the survivors of a broken nation - people caught between factions, conflict, and the slow collapse of their society. Most have moved out from the cities and live in small settlements, or scattered farms, trying to rebuild some form of normal life in a world ruled by scarcity and fear.

          Some civilians still cling to the old laws and values, while others have adapted to the new order, dealing with smugglers, prostitutes, mercenaries, and armed groups just to survive. Bartering, scavenging, and quiet cooperation are part of daily life. Trust is rare, and everyone keeps one eye on the horizon - for soldiers, raiders, or worse.

          Civilians trade, gather information, work as mechanics or medics. Some may secretly support one faction, while others simply try to stay neutral and alive.

        </pre>
        <hr>
        <div class="post-content">
          <table class="info-table">
            <tr>
              <td class="label-col"><b>Unit description</b></td>
              <td class="value-col">Civilians trade, gather information, work as mechanics, traders, farmers, carpenters
                or medics. Some may secretly support one faction, while others simply try to stay neutral and alive.
              </td>
            </tr>
            <tr>
              <td class="label-col"><b>Unit size</b></td>
              <td class="value-col">100</td>
            </tr>
            <tr>
              <td class="label-col"><b>Vehicle types</b></td>
              <td class="value-col">Civilian cars, pickups, jeeps.</td>
            </tr>
            <tr>
              <td class="label-col"><b>Armband color</b></td>
              <td class="value-col">White & Green</td>
            </tr>
            <tr>
              <td class="label-col"><b>Armband marking</b></td>
              <td class="value-col">Player number</td>
            </tr>
            <tr>
              <td class="label-col"><b>Uniform types</b></td>
              <td class="value-col">Civilian clothes</td>
            </tr>
            <tr>
              <td class="label-col"><b>Equipment style</b></td>
              <td class="value-col">None</td>
            </tr>
            <tr>
              <td class="label-col"><b>Armament</b></td>
              <td class="value-col">Small arms or hunting rifles</td>
            </tr>
            <tr>
              <td class="label-col"><b>Expected playstyle</b></td>
              <td class="value-col">LARP</td>
            </tr>
            <tr>
              <td class="label-col"><b>Physical requirement level</b></td>
              <td class="value-col">Low</td>
            </tr>
            <tr>
              <td class="label-col"><b>LARP elements</b></td>
              <td class="value-col">Players create and play their own characters in the game.</td>
            </tr>
            <tr>
              <td class="label-col"><b>Base & Logistics</b></td>
              <td class="value-col">Players are able to book a free spot in Berget-provided tents, drinking water, 230V
                power for battery charging & toilets will be provided to a civilian campsite. Civilians can also operate
                self-sufficiently and build camps. Small civilian tents allowed for personal base. Fresh water will be
                provided at key objectives. Shower and kiosk in the Safezone.
              </td>
            </tr>
            <tr>
              <td class="label-col"><b>Supply of heavy anti-tank weapons</b></td>
              <td class="value-col">None</td>
            </tr>
            <tr>
              <td class="label-col"><b>Supply of light tank weapons</b></td>
              <td class="value-col">None</td>
            </tr>
            <tr>
              <td class="label-col"><b>Mandatory equipment to be brought by the player</b></td>
              <td class="value-col">Civilian clothes</td>
            </tr>
            <tr>
              <td class="label-col"><b>Team equipment provided by Berget</b></td>
              <td class="value-col">Drugs, Berget dollars. Various items to sell or negotiate with.</td>
            </tr>
            <tr>
              <td class="label-col"><b>Unit special requirements</b></td>
              <td class="value-col">Players create and play their own characters in the game. Players should have a
                backstory and be able to adapt their character based on in game events. BE will provide small missions
                during the game.
              </td>
            </tr>
          </table>
        </div>
      </div>
    </div>

    <div id="committee" class="post">
      <div class="post-content-wrapper">
        <div class="post-header">Civil Election Committee (CEC)</div>

        <pre class="post-content">
          The <b>Civil Election Committee (CEC)</b> consists of Election observers & workers, Politicians and the Civil Protection Bureau. The committee is responsible for organizing and overseeing the regional election in Nordland. Operating with limited resources and under pressure from armed factions, they work to restore civilian authority through democratic local governance. Members handle voter registration, election logistics, and ensure ballot integrity.

          Working with minimal resources and under constant pressure from armed factions, the committee's mission is to restore civilian authority through local governance. The Civil Protection Bureau provides armed security and escorts during election operations, making them one of the last functioning democratic institutions in Nordland.

          Committee members coordinate between settlements, maintain voter records, and manage polling stations. Their work symbolizes the last flicker of democracy in a country ruled by uncertainty as they face daily challenges from factions attempting to influence or disrupt the electoral process while trying to uphold democratic principles.
        </pre>
      </div>
    </div>

    <div id="observer" class="post">
      <div class="post-content-wrapper">
        <div class="post-header">Election Observer & Worker</div>

        <pre class="post-content">
          The <b>Election Observers</b> are neutral monitors tasked with ensuring fairness and transparency in the regional election. They move between polling sites, villages, and checkpoints, documenting irregularities and reporting interference. To some, they are guardians of justice - to others, inconvenient witnesses. Their presence can change the balance of power, making them both respected and hunted.

          Working independently from but alongside the Civil Protection Bureau who provide armed security and escorts when needed, the Observers maintain strict neutrality while investigating claims of voter suppression, intimidation, or fraud. Their mission is to help ensure a free and fair electoral process despite the ongoing instability.

          Observers document election activities, interview civilians, and file detailed reports about the conduct of all parties involved. They face daily risks from factions attempting to influence or obstruct their work while striving to uphold democratic principles through impartial oversight.
        </pre>
        <hr>
        <div class="post-content">
          <table class="info-table">
            <tr>
              <td class="label-col"><b>Unit description</b></td>
              <td class="value-col">The unit is responsible for organizing and overseeing the regional election,
                ensuring that it is conducted properly, transparently, and without interference or fraud.
              </td>
            </tr>
            <tr>
              <td class="label-col"><b>Unit size</b></td>
              <td class="value-col">20</td>
            </tr>
            <tr>
              <td class="label-col"><b>Vehicle types</b></td>
              <td class="value-col">Civilian cars, pickups, jeeps.</td>
            </tr>
            <tr>
              <td class="label-col"><b>Armband color</b></td>
              <td class="value-col">White & Green</td>
            </tr>
            <tr>
              <td class="label-col"><b>Armband marking</b></td>
              <td class="value-col">Player number</td>
            </tr>
            <tr>
              <td class="label-col"><b>Uniform types</b></td>
              <td class="value-col">Civilian clothes</td>
            </tr>
            <tr>
              <td class="label-col"><b>Equipment style</b></td>
              <td class="value-col">Well dressed civilian clothes, shirt or polo shirt with Election LOGO (Will be
                provided by Berget-Events)
              </td>
            </tr>
            <tr>
              <td class="label-col"><b>Armament</b></td>
              <td class="value-col">None</td>
            </tr>
            <tr>
              <td class="label-col"><b>Expected playstyle</b></td>
              <td class="value-col">LARP</td>
            </tr>
            <tr>
              <td class="label-col"><b>Physical requirement level</b></td>
              <td class="value-col">Low</td>
            </tr>
            <tr>
              <td class="label-col"><b>LARP elements</b></td>
              <td class="value-col">Players create and play their own characters in the game.</td>
            </tr>
            <tr>
              <td class="label-col"><b>Base & Logistics</b></td>
              <td class="value-col">Players are able to book a free spot in Berget-provided tents, drinking water, 230V
                power for battery charging & toilets will be provided to a civilian campsite. Civilians can also operate
                self-sufficiently and build camps. Small civilian tents allowed for personal base. Fresh water will be
                provided at key objectives. Shower and kiosk in the Safezone.
              </td>
            </tr>
            <tr>
              <td class="label-col"><b>Supply of heavy anti-tank weapons</b></td>
              <td class="value-col">None</td>
            </tr>
            <tr>
              <td class="label-col"><b>Supply of light tank weapons</b></td>
              <td class="value-col">None</td>
            </tr>
            <tr>
              <td class="label-col"><b>Mandatory equipment to be brought by the player</b></td>
              <td class="value-col">Civilian clothes</td>
            </tr>
            <tr>
              <td class="label-col"><b>Team equipment provided by Berget</b></td>
              <td class="value-col">Election ballots and posters. Various items to sell or negotiate with.</td>
            </tr>
            <tr>
              <td class="label-col"><b>Unit special requirements</b></td>
              <td class="value-col">Players create and play their own characters in the game. Players should have a
                backstory and be able to adapt their character based on in game events. BE will provide small missions
                during the game.
              </td>
            </tr>
          </table>
        </div>
      </div>
    </div>

    <div id="politicians" class="post">
      <div class="post-content-wrapper">
        <div class="post-header">Politicians</div>

        <pre class="post-content">
          The <b>Politicians</b> in Nordland's regional election are local figures - mayors, former administrators, or ambitious newcomers - seeking to shape the future of their communities. Some fight for reconstruction and stability, while others see the election as a means to gain control or legitimacy. Their influence depends not only on words and promises but on who they can convince - or buy - to stand by them.

          These candidates navigate a complex web of alliances and rivalries, balancing civilian needs against faction demands. Many walk a dangerous line between different power groups, promising change while avoiding antagonizing armed groups who could disrupt their campaigns or worse.

          The politicians must build support through speeches, deals, and demonstrations of capability. Some focus on basic needs like security and services, while others promote grander visions of Nordland's future. Success requires both popular appeal and practical ability to deliver on promises in a fractured society.
        </pre>
        <hr>
        <div class="post-content">
          <table class="info-table">
            <tr>
              <td class="label-col"><b>Unit description</b></td>
              <td class="value-col">The unit forms political parties and campaigns in the regional election, seeking
                influence and control through political means.
              </td>
            </tr>
            <tr>
              <td class="label-col"><b>Unit size</b></td>
              <td class="value-col">20</td>
            </tr>
            <tr>
              <td class="label-col"><b>Vehicle types</b></td>
              <td class="value-col">Civilian cars, pickups, jeeps.</td>
            </tr>
            <tr>
              <td class="label-col"><b>Armband color</b></td>
              <td class="value-col">White & Green</td>
            </tr>
            <tr>
              <td class="label-col"><b>Armband marking</b></td>
              <td class="value-col">Player number</td>
            </tr>
            <tr>
              <td class="label-col"><b>Uniform types</b></td>
              <td class="value-col">Dressed up, suit or and tie.</td>
            </tr>
            <tr>
              <td class="label-col"><b>Equipment style</b></td>
              <td class="value-col">Well dressed civilian clothes with their political party's logo.</td>
            </tr>
            <tr>
              <td class="label-col"><b>Armament</b></td>
              <td class="value-col">None</td>
            </tr>
            <tr>
              <td class="label-col"><b>Expected playstyle</b></td>
              <td class="value-col">LARP</td>
            </tr>
            <tr>
              <td class="label-col"><b>Physical requirement level</b></td>
              <td class="value-col">Low</td>
            </tr>
            <tr>
              <td class="label-col"><b>LARP elements</b></td>
              <td class="value-col">Players create and play their own characters in the game.</td>
            </tr>
            <tr>
              <td class="label-col"><b>Base & Logistics</b></td>
              <td class="value-col">Players are able to book a free spot in Berget-provided tents, drinking water, 230V
                power for battery charging & toilets will be provided to a civilian campsite. Civilians can also operate
                self-sufficiently and build camps. Small civilian tents allowed for personal bases. Fresh water will be
                provided at key objectives. Shower and kiosk in the Safezone.
              </td>
            </tr>
            <tr>
              <td class="label-col"><b>Supply of heavy anti-tank weapons</b></td>
              <td class="value-col">None</td>
            </tr>
            <tr>
              <td class="label-col"><b>Supply of light tank weapons</b></td>
              <td class="value-col">None</td>
            </tr>
            <tr>
              <td class="label-col"><b>Mandatory equipment to be brought by the player</b></td>
              <td class="value-col">Civilian clothes. A well written party agenda with logo and posters, connected to
                the storyline.
              </td>
            </tr>
            <tr>
              <td class="label-col"><b>Team equipment provided by Berget</b></td>
              <td class="value-col">Various items to sell or negotiate with.</td>
            </tr>
            <tr>
              <td class="label-col"><b>Unit special requirements</b></td>
              <td class="value-col">Players create and play their own characters in the game. Players should have a
                backstory and be able to adapt their character based on in game events. BE will provide small missions
                during the game.
              </td>
            </tr>
          </table>
        </div>
      </div>
    </div>

    <div id="bureau" class="post">
      <div class="post-content-wrapper">
        <div class="post-header">Civil Protection Bureau (CPB)</div>

        <pre class="post-content">
          The <b>Civil Protection Bureau (CPB)</b> serves as the armed security branch of the civilian administration. Their primary duty is to protect election officials, politicians, and voting sites.
        </pre>
        <hr>
        <div class="post-content">
          <table class="info-table">
            <tr>
              <td class="label-col"><b>Unit description</b></td>
              <td class="value-col">Provides security for the election workers, observers and politicians as well as
                securing the voting sites.
              </td>
            </tr>
            <tr>
              <td class="label-col"><b>Unit size</b></td>
              <td class="value-col">20</td>
            </tr>
            <tr>
              <td class="label-col"><b>Vehicle types</b></td>
              <td class="value-col">Civilian cars up to class 1</td>
            </tr>
            <tr>
              <td class="label-col"><b>Armband color</b></td>
              <td class="value-col">White & Green</td>
            </tr>
            <tr>
              <td class="label-col"><b>Armband marking</b></td>
              <td class="value-col">Player number</td>
            </tr>
            <tr>
              <td class="label-col"><b>Uniform types</b></td>
              <td class="value-col">Civilian black clothes, black pants, black shirt with black tactical vest.</td>
            </tr>
            <tr>
              <td class="label-col"><b>Equipment style</b></td>
              <td class="value-col">Private security force</td>
            </tr>
            <tr>
              <td class="label-col"><b>Armament</b></td>
              <td class="value-col">Small arms</td>
            </tr>
            <tr>
              <td class="label-col"><b>Expected playstyle</b></td>
              <td class="value-col">LARP & Action</td>
            </tr>
            <tr>
              <td class="label-col"><b>Physical requirement level</b></td>
              <td class="value-col">Medium</td>
            </tr>
            <tr>
              <td class="label-col"><b>LARP elements</b></td>
              <td class="value-col">Yes</td>
            </tr>
            <tr>
              <td class="label-col"><b>Base & Logistics</b></td>
              <td class="value-col">Players are able to book a free spot in Berget-provided tents, drinking water, 230V
                power for battery charging & toilets will be provided to a civilian campsite. Civilians can also operate
                self-sufficiently and build camps. Small civilian tents allowed for personal base. Fresh water will be
                provided at key objectives. Shower and kiosk in the Safezone.
              </td>
            </tr>
            <tr>
              <td class="label-col"><b>Supply of heavy anti-tank weapons</b></td>
              <td class="value-col">None</td>
            </tr>
            <tr>
              <td class="label-col"><b>Supply of light tank weapons</b></td>
              <td class="value-col">None</td>
            </tr>
            <tr>
              <td class="label-col"><b>Mandatory equipment to be brought by the player</b></td>
              <td class="value-col">-</td>
            </tr>
            <tr>
              <td class="label-col"><b>Team equipment provided by Berget</b></td>
              <td class="value-col">-</td>
            </tr>
            <tr>
              <td class="label-col"><b>Unit special requirements</b></td>
              <td class="value-col">Players create and play their own characters in the game. Players should have a
                backstory and be able to adapt their character based on in game events. BE will provide small missions
                during the game.
              </td>
            </tr>
          </table>
        </div>
      </div>
    </div>

    <div id="gangs" class="post">
      <div class="post-content-wrapper">
        <div class="post-header">Gangs of Nordland</div>

        <pre class="post-content">
          As Nordland sinks deeper into decline, its highways and isolated towns have fallen under the rule of two dominant outlaw groups: <b>Odin’s Wolves MC</b> and <b>Sons of the North</b>.

          The <b>Odin’s Wolves MC</b>, made up of hardened ex-soldiers turned drug lords, control the northern routes and highland compounds with military precision. Their leader enforces discipline through fear and loyalty, running the narcotics trade like a battlefield campaign. They claim to bring order - but their empire is built on addiction and violence.

          The <b>Sons of the North</b>, on the other hand, operate like a northern mafia. Focused on smuggling, extortion, and control of black-market trade, they move quietly, buying influence and eliminating threats with calculated efficiency. To them, chaos is just another business opportunity.

          Their rivalry fuels constant tension - the Wolves rule through force, the Sons through influence. Both maintain uneasy ties with the <b>Yellow faction (Mercenaries)</b> when it suits them, trading weapons, drugs, or information. While being constantly hunted by the Nordland Defence Group.
        </pre>
        <div class="post-image-wrapper">
          <img src="@/assets/2026/odins_wolves.jpg" alt="Odin’s Wolves MC" />
          <img src="@/assets/2026/sons_of_the_north.jpg" alt="Sons of the North" />
        </div>
      </div>
    </div>

    <div id="wolves" class="post">
      <div class="post-content-wrapper">
        <div class="post-header">Odin’s Wolves MC</div>

        <pre class="post-content">
          Once soldiers and survivalists, now kings of the underworld. The Odin’s Wolves MC control the northern drug trade - running narcotics through abandoned highways and across the borders under cover of darkness. The Wolves don’t pretend to be saviors; they sell addiction for profit and power, and they do it with military efficiency. Their loyalty is bought in blood and money, and anyone cutting into their business vanishes into the cold.
        </pre>
        <hr>
        <div class="post-content">
          <table class="info-table">
            <tr>
              <td class="label-col"><b>Unit description</b></td>
              <td class="value-col">Former soldiers turned drug traffickers, the Odin's Wolves MC control production and
                distribution across the north, enforcing their trade with violent discipline.
              </td>
            </tr>
            <tr>
              <td class="label-col"><b>Unit size</b></td>
              <td class="value-col">100</td>
            </tr>
            <tr>
              <td class="label-col"><b>Vehicle types</b></td>
              <td class="value-col">Motorcycles, civilian cars, pickups, jeeps.</td>
            </tr>
            <tr>
              <td class="label-col"><b>Armband color</b></td>
              <td class="value-col">Red & White</td>
            </tr>
            <tr>
              <td class="label-col"><b>Armband marking</b></td>
              <td class="value-col">Player number</td>
            </tr>
            <tr>
              <td class="label-col"><b>Uniform types</b></td>
              <td class="value-col">Black clothes, Biker clothes</td>
            </tr>
            <tr>
              <td class="label-col"><b>Equipment style</b></td>
              <td class="value-col">None</td>
            </tr>
            <tr>
              <td class="label-col"><b>Armament</b></td>
              <td class="value-col">Small arms, assault rifles</td>
            </tr>
            <tr>
              <td class="label-col"><b>Expected playstyle</b></td>
              <td class="value-col">Action & LARP</td>
            </tr>
            <tr>
              <td class="label-col"><b>Physical requirement level</b></td>
              <td class="value-col">Normal</td>
            </tr>
            <tr>
              <td class="label-col"><b>LARP elements</b></td>
              <td class="value-col">-</td>
            </tr>
            <tr>
              <td class="label-col"><b>Base & Logistics</b></td>
              <td class="value-col">Players are able to book a free spot in Berget-provided tents, drinking water, 230V
                power for battery charging & toilets will be provided to a civilian campsite. Civilians can also operate
                self-sufficiently and build camps. Small civilian tents allowed for personal base. Fresh water will be
                provided at key objectives. Shower and kiosk in the Safezone.
              </td>
            </tr>
            <tr>
              <td class="label-col"><b>Supply of heavy anti-tank weapons</b></td>
              <td class="value-col">None</td>
            </tr>
            <tr>
              <td class="label-col"><b>Supply of light tank weapons</b></td>
              <td class="value-col">Odin's wolfs team gets 5% of the total supply. This unit can book and use light
                anti-tank weapons.
              </td>
            </tr>
            <tr>
              <td class="label-col"><b>Mandatory equipment to be brought by the player</b></td>
              <td class="value-col">None.</td>
            </tr>
            <tr>
              <td class="label-col"><b>Team equipment provided by Berget</b></td>
              <td class="value-col">Drugs, Berget dollars. Various items to sell or negotiate with.</td>
            </tr>
            <tr>
              <td class="label-col"><b>Unit special requirements</b></td>
              <td class="value-col">Players need to use the gang logo.</td>
            </tr>
          </table>
        </div>
      </div>
    </div>

    <div id="sons" class="post">
      <div class="post-content-wrapper">
        <div class="post-header">Sons of the North</div>

        <pre class="post-content">
          Less a gang, more a criminal syndicate. The Sons of the North operate like a northern mafia - organized, ruthless, and deeply entrenched in Nordland’s decaying economy. They deal in smuggling, extortion, and protection rackets, maintaining order in chaos through fear and calculated control. Every deal, every favor, every death is part of a larger plan. Their leadership sees themselves as the true inheritors of Nordland’s legacy - businessmen in a broken world, building a new order one crime at a time. Where the Wolves bring fire and noise, the Sons move quietly, tightening their grip until resistance becomes impossible.
        </pre>
        <hr>
        <div class="post-content">
          <table class="info-table">
            <tr>
              <td class="label-col"><b>Unit description</b></td>
              <td class="value-col">An organized criminal syndicate controlling smuggling, extortion, and black-market
                trade across Nordland. The Sons of the North operate through influence, money, and calculated violence.
              </td>
            </tr>
            <tr>
              <td class="label-col"><b>Unit size</b></td>
              <td class="value-col">100</td>
            </tr>
            <tr>
              <td class="label-col"><b>Vehicle types</b></td>
              <td class="value-col">Motorcycles, civilian cars, pickups, jeeps.</td>
            </tr>
            <tr>
              <td class="label-col"><b>Armband color</b></td>
              <td class="value-col">Black & White</td>
            </tr>
            <tr>
              <td class="label-col"><b>Armband marking</b></td>
              <td class="value-col">Player number</td>
            </tr>
            <tr>
              <td class="label-col"><b>Uniform types</b></td>
              <td class="value-col">PMC style, camo pants civilian upper.</td>
            </tr>
            <tr>
              <td class="label-col"><b>Equipment style</b></td>
              <td class="value-col">Chest rigs, plate carriers.</td>
            </tr>
            <tr>
              <td class="label-col"><b>Armament</b></td>
              <td class="value-col">Small arms, assault rifles and machineguns.</td>
            </tr>
            <tr>
              <td class="label-col"><b>Expected playstyle</b></td>
              <td class="value-col">Action & LARP</td>
            </tr>
            <tr>
              <td class="label-col"><b>Physical requirement level</b></td>
              <td class="value-col">Normal</td>
            </tr>
            <tr>
              <td class="label-col"><b>LARP elements</b></td>
              <td class="value-col">-</td>
            </tr>
            <tr>
              <td class="label-col"><b>Base & Logistics</b></td>
              <td class="value-col">Players are able to book a free spot in Berget-provided tents, drinking water, 230V
                power for battery charging & toilets will be provided to a civilian campsite. Civilians can also operate
                self-sufficiently and build camps. Small civilian tents allowed for personal base. Fresh water will be
                provided at key objectives. Shower and kiosk in the Safezone.
              </td>
            </tr>
            <tr>
              <td class="label-col"><b>Supply of heavy anti-tank weapons</b></td>
              <td class="value-col">Sons of the North team gets 10 % of the total supply. This unit can book and use
                anti-tank weapons.
              </td>
            </tr>
            <tr>
              <td class="label-col"><b>Supply of light tank weapons</b></td>
              <td class="value-col">Sons of the North team gets 5% of the total supply. This unit can book and use light
                anti-tank weapons.
              </td>
            </tr>
            <tr>
              <td class="label-col"><b>Mandatory equipment to be brought by the player</b></td>
              <td class="value-col">None.</td>
            </tr>
            <tr>
              <td class="label-col"><b>Team equipment provided by Berget</b></td>
              <td class="value-col">Drugs, Berget dollars. Various items to sell or negotiate with.</td>
            </tr>
            <tr>
              <td class="label-col"><b>Unit special requirements</b></td>
              <td class="value-col">Players need to use the gang logo.</td>
            </tr>
          </table>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'Civilian'
}
</script>

<style scoped>
b { color: #A7C8A0; }
@media (min-width: 921px) {
  video {
    margin-top: 88px;
  }
  .post-image-wrapper {
    margin-right: -30px
  }
}
.post-image-wrapper img {
  width: 50%;
}
</style>
